import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Me } from "../api/auth.js";
import { ME_KEY } from "../data/account.js";
import { joinAdminRecovery, needsAutoEnroll } from "./adminRecovery.js";
import { useSession } from "./session.js";

/**
 * Enrolls the account in admin-assisted recovery on its own when the policy requires it. One
 * attempt per instance key and session; a failure waits for the next unlock.
 */
export function useAutoEnrollment(me: Me | undefined): void {
  const { state } = useSession();
  const queryClient = useQueryClient();
  const attempted = useRef<string | undefined>(undefined);
  const status = me?.adminRecovery ?? null;
  const required = me?.policies.requireAdminRecovery === true;
  const client = state.status === "unlocked" ? state.client : undefined;

  useEffect((): void => {
    if (client === undefined || !needsAutoEnroll(status, required) || attempted.current === status.keyId) {
      return;
    }
    attempted.current = status.keyId;
    void joinAdminRecovery(client, status)
      .then((): Promise<void> => queryClient.invalidateQueries({ queryKey: ME_KEY }))
      .catch((): void => undefined);
  }, [client, status, required, queryClient]);
}
